/**
 * Authentication Helper
 * Dynamically constructs auth URLs without hardcoding domains
 */

import { LOGIN_URL, ID_URL } from '../config';

/**
 * Get the current domain dynamically (works in all environments)
 */
export const getCurrentDomain = (): string => {
  return window.location.origin;
};

/**
 * Build login redirect URL dynamically
 * DIRECT MODE: Bypasses validate-login-standalone.php, React handles auth via useNowIdLogin
 * @param lang - Language code ('en' or 'zh')
 * @param redirectTo - Where to redirect after successful login (default: current page)
 * @param cancelUrl - Where to redirect if user cancels (default: current page)
 * @returns Full login URL with redirect parameters
 */
export const buildLoginUrl = (
  lang: 'en' | 'zh' = 'en',
  redirectTo?: string,
  cancelUrl?: string
): string => {
  const currentDomain = getCurrentDomain();
  const currentPath = window.location.pathname;
  
  // Default redirect destinations
  const finalRedirectTo = redirectTo || currentPath || '/';
  const finalCancelUrl = cancelUrl || currentPath || '/';
  
  // CRITICAL: Redirect must be absolute URL with full domain
  // This is where id.now.com will redirect after successful authentication
  const absoluteRedirectTo = finalRedirectTo.startsWith('http') 
    ? finalRedirectTo 
    : `${currentDomain}${finalRedirectTo.startsWith('/') ? '' : '/'}${finalRedirectTo}`;
  
  // DIRECT MODE: Point redirect straight to React app (no PHP middleware)
  // NowID will set NOWSESSIONID cookie, then React's Header.tsx will:
  // 1. Detect NOWSESSIONID cookie
  // 2. Call useNowIdLogin.login(sessionId, userAgent)
  // 3. useNowIdLogin calls nowid-proxy.php to verify token and get profile
  // 4. Store auth data in window.__NOWTV_AUTH__
  
  // Build the full login URL
  const loginBaseUrl = LOGIN_URL || `${ID_URL}nowidform/login.form`;
  return `${loginBaseUrl}?lang=${lang}&cancel=${encodeURIComponent(currentDomain + finalCancelUrl)}&redirect=${encodeURIComponent(absoluteRedirectTo)}`;
};

/**
 * Build logout URL dynamically
 * @param lang - Language code ('en' or 'zh')
 * @param redirectTo - Where to redirect after logout (default: home)
 * @returns Full logout URL
 */
export const buildLogoutUrl = (
  lang: 'en' | 'zh' = 'en',
  redirectTo?: string
): string => {
  const currentDomain = getCurrentDomain();
  const finalRedirectTo = redirectTo || '/';
  
  const absoluteRedirect = finalRedirectTo.startsWith('http')
    ? finalRedirectTo
    : `${currentDomain}${finalRedirectTo.startsWith('/') ? '' : '/'}${finalRedirectTo}`;

  const logoutBaseUrl = ID_URL || 'https://id.now.com/';
  return `${logoutBaseUrl}nowidform/logout.do?redirect=${encodeURIComponent(absoluteRedirect)}`;
};

/**
 * Navigate to login page
 * @param lang - Language code ('en' or 'zh')
 * @param redirectTo - Where to redirect after successful login
 */
export const navigateToLogin = (
  lang: 'en' | 'zh' = 'en',
  redirectTo?: string
): void => {
  const loginUrl = buildLoginUrl(lang, redirectTo);
  window.location.href = loginUrl;
};
